import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Shield, Zap, Award, User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatApiError } from '../services/api';

const Login = () => {
  const { loginWithEmail, register } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        if (!name.trim()) { setError('Digite seu nome'); setLoading(false); return; }
        await register(name, email, password);
      } else {
        await loginWithEmail(email, password);
      }
      navigate('/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Erro ao conectar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 py-20">
      <div className="max-w-5xl w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side */}
          <div className="text-center md:text-left">
            <img 
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_07e3fa1c-accb-40af-b9f6-b18f873b4bec/artifacts/3s7e8l1h_logodas.png" 
              alt="Cloud Cheats" 
              className="h-32 w-auto mx-auto md:mx-0 mb-8 drop-shadow-2xl"
            />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 gaming-title">
              BEM-VINDO A <span className="text-red-600 glow-red">CLOUD CHEATS</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              {isRegister ? 'Crie sua conta e comece a dominar' : 'Entre na sua conta para continuar'}
            </p>
            <div className="space-y-4">
              {[
                { icon: Shield, text: 'Pagamento 100% Seguro' },
                { icon: Zap, text: 'Entrega Instantanea Apos Pagamento' },
                { icon: Award, text: 'Suporte Premium 24/7' }
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-red-600" />
                  </div>
                  <span className="font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-gray-900 rounded-2xl p-10 border-2 border-red-900/30 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-3 gaming-title">
                {isRegister ? 'CRIAR CONTA' : 'ENTRAR'}
              </h2>
              <p className="text-gray-400 text-sm">
                {isRegister ? 'Preencha seus dados para criar sua conta' : 'Use seu email e senha para entrar'}
              </p>
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 mb-6">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Seu nome"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-gray-800 border-2 border-gray-700 rounded-xl pl-12 pr-4 py-4 text-white placeholder-gray-500 focus:border-red-600 focus:outline-none"
                    data-testid="register-name-input"
                  />
                </div>
              )}

              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-gray-800 border-2 border-gray-700 rounded-xl pl-12 pr-4 py-4 text-white placeholder-gray-500 focus:border-red-600 focus:outline-none"
                  data-testid="login-email-input"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-gray-800 border-2 border-gray-700 rounded-xl pl-12 pr-12 py-4 text-white placeholder-gray-500 focus:border-red-600 focus:outline-none"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-lg rounded-xl gaming-title"
                data-testid="login-submit-btn"
              >
                {loading ? 'CARREGANDO...' : isRegister ? 'CRIAR CONTA' : 'ENTRAR'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => { setIsRegister(!isRegister); setError(''); }}
                className="text-gray-400 hover:text-red-500 transition-colors text-sm"
                data-testid="toggle-auth-mode"
              >
                {isRegister ? 'Ja tem conta? ' : 'Nao tem conta? '}
                <span className="text-red-500 font-bold">{isRegister ? 'Entrar' : 'Criar conta'}</span>
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-red-900/20">
              <p className="text-center text-xs text-gray-500">
                Seus dados estao protegidos e seguros.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
